import { ref, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import * as PleasedApi from '@/common/api/api.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { ChatRateMode } from '@/common/api/models.pleased';
import { devLog } from '@/common/debug';

/**
 * Composable for fetching feedback selections with mocked flow support
 * Automatically uses mocked data when in mocked flow mode
 */
export const useFeedbackSelections = (entityType?: ChatRateMode) => {
  const feedbackSelections = ref<PleasedApi.Model.FeedbackCategory[]>([]);
  const isLoading = ref(false);

  const mockedFlowStore = useMockedFlowStore();
  const {
    isActive: isMockedFlowActive,
    hasFeedbackSelections: hasMockedFeedbackSelections,
    feedbackSelections: mockedFeedbackSelections,
  } = storeToRefs(mockedFlowStore);

  const fetchFeedbackSelections = async () => {
    isLoading.value = true;

    try {
      // Check if we're in mocked mode
      if (isMockedFlowActive.value) {
        if (!hasMockedFeedbackSelections.value || !mockedFeedbackSelections.value) {
          return;
        }

        devLog.debug('[FEEDBACK_SELECTIONS] Using mocked feedback selections');
        feedbackSelections.value = mockedFeedbackSelections.value;
        return;
      }

      // Fetch real data from API
      if (entityType === 'AI') {
        feedbackSelections.value = (await PleasedApi.getAiFeedbackSelections()) || [];
      } else {
        feedbackSelections.value = (await PleasedApi.getFeedbackSelections()) || [];
      }
    } finally {
      isLoading.value = false;
    }
  };

  onMounted(fetchFeedbackSelections);

  return {
    feedbackSelections,
    isLoading,
    refetch: fetchFeedbackSelections,
  };
};
